import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FULFILMENT_LABELS, ORDER_STATUS_LABELS, formatEuro, type DashboardStats, type FulfilmentMethod, type OrderStatus } from '@casa-te/shared';
import { supabase, unwrap } from '../lib/supabase';
import { useAsync, useStores } from '../lib/data';
import { useAuth } from '../lib/auth';
import { t, dateLocale } from '../lib/i18n';
import { Empty, Loading, Notice } from '../components/ui';
import { Icon, type AdminIcon } from '../components/Icon';

const RANGES = [
  { id: 'today', label: 'Oggi', days: 0 },
  { id: '7d', label: '7 giorni', days: 7 },
  { id: '30d', label: '30 giorni', days: 30 },
  { id: '90d', label: '90 giorni', days: 90 },
];
const FULFILMENT_COLORS: Record<FulfilmentMethod, string> = { home: '#2f6634', pickup: '#8fb46b', store: '#d9ee2f' };
const PIPELINE: Array<{ status: OrderStatus; color: string }> = [
  { status: 'paid', color: '#2f7fd1' }, { status: 'picking', color: '#7b4bc4' },
  { status: 'ready', color: '#5d9a1d' }, { status: 'shipped', color: '#6f786f' },
];

function rangeStart(days: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
}
const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayLabel = (key: string) => new Date(`${key}T12:00:00`).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' });

function Kpi({ icon, label, value, hint, tone }: { icon: AdminIcon; label: string; value: string; hint?: string; tone?: 'hero' | 'warn' }) {
  return <div className={`card kpi-card ${tone ?? ''}`}>
    <div className="kpi-top"><span className="kpi-icon"><Icon name={icon} /></span><span className="label">{label}</span></div>
    <div className="value">{value}</div>
    {hint && <div className="hint">{hint}</div>}
  </div>;
}

export function AnalyticsPage() {
  const { can } = useAuth();
  const stores = useStores();
  const [range, setRange] = useState(RANGES[1]);
  const [storeId, setStoreId] = useState<string>('');
  const stats = useAsync(async () => unwrap(await supabase.rpc('admin_dashboard', {
    p_from: rangeStart(range.days).toISOString(), p_to: new Date(Date.now() + 60_000).toISOString(), p_store_id: storeId || null,
  })) as DashboardStats, [range.id, storeId]);
  const s = stats.data;
  const open = s?.open_orders ?? {};

  // Zero-filled daily series across the whole range, so the chart reads as a timeline.
  const byDay = new Map((s?.by_day ?? []).map((d) => [d.day.slice(0, 10), d]));
  const days: Array<{ day: string; revenue_cents: number; orders: number }> = [];
  for (let d = rangeStart(range.days); d <= new Date(); d.setDate(d.getDate() + 1)) {
    const key = dayKey(d);
    days.push(byDay.get(key) ?? { day: key, revenue_cents: 0, orders: 0 });
  }
  const maxDay = Math.max(1, ...days.map((d) => d.revenue_cents));
  const fulfilment = Object.entries(s?.by_fulfilment ?? {}) as Array<[FulfilmentMethod, number]>;
  const fulfilmentTotal = fulfilment.reduce((n, [, v]) => n + v, 0);
  const topMax = Math.max(1, ...(s?.top_products ?? []).map((p) => p.quantity)); // ranked by pieces sold, so bars use pieces too
  const toWork = (open.paid ?? 0) + (open.picking ?? 0);

  return <>
    <div className="dash-head">
      <div>
        <h1>{t('Analisi dati')}</h1>
        <p className="muted" style={{ margin: 0 }}>{t('Incassi, consegne e prodotti più venduti nel periodo scelto.')}</p>
      </div>
      <div className="row">
        <div className="segmented" role="group" aria-label={t('Periodo')}>
          {RANGES.map((r) => <button key={r.id} aria-pressed={r.id === range.id}
            className={r.id === range.id ? 'on' : ''} onClick={() => setRange(r)}>{t(r.label)}</button>)}
        </div>
        {can('admin', 'manager') && <select value={storeId} onChange={(e) => setStoreId(e.target.value)} aria-label={t('Negozio')}>
          <option value="">{t('Tutti i negozi')}</option>{stores.data?.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}</select>}
      </div>
    </div>

    {stats.error && <Notice tone="error">{stats.error}</Notice>}
    {!s ? <Loading /> : <>
      <div className="grid kpi">
        <Kpi tone="hero" icon="euro" label={t('Incasso netto')} value={formatEuro(s.revenue_cents)} hint={t('al netto dei rimborsi')} />
        <Kpi icon="bag" label={t('Ordini pagati')} value={String(s.orders)} hint={toWork ? t('{n} da preparare', { n: toWork }) : t('nessuno da preparare')} />
        <Kpi icon="receipt" label={t('Scontrino medio')} value={formatEuro(s.average_order_cents)} hint={t('per ordine pagato')} />
        <Kpi icon="refund" label={t('Rimborsi')} value={formatEuro(s.refunded_cents)} hint={s.refunded_cents ? t('emessi su Stripe') : t('nessun rimborso')} />
      </div>

      <div className="pipeline">
        {PIPELINE.map(({ status, color }, i) => <Link key={status} to={`/orders?status=${status}`} className="stage">
          <span className="dot" style={{ background: color }} />
          <span className="stage-label">{t(ORDER_STATUS_LABELS[status])}</span>
          <span className="stage-value">{open[status] ?? 0}</span>
          {i < PIPELINE.length - 1 && <span className="stage-arrow"><Icon name="arrow" size={14} /></span>}
        </Link>)}
      </div>

      <div className="grid dash-main">
        <div className="card">
          <div className="card-head"><h2>{t('Incasso per giorno')}</h2>
            <span className="muted small">{days.length > 1 ? `${dayLabel(days[0].day)} – ${dayLabel(days.at(-1)!.day)}` : dayLabel(days[0].day)}</span></div>
          <div className="chart">
            <div className="chart-grid">{[1, 0.5, 0].map((f) =>
              <div key={f} className="gridline"><span>{formatEuro(Math.round(maxDay * f))}</span></div>)}</div>
            <p className="sr-only">{t('Incasso per giorno: {list}.', { list: days.filter((d) => d.revenue_cents).map((d) => `${dayLabel(d.day)} ${formatEuro(d.revenue_cents)}`).join(', ') || t('nessuna vendita nel periodo') })}</p>
            <div className="bars" aria-hidden="true">{days.map((d) =>
              <div key={d.day} className="bar-col" title={t('{day}: {amount} · {n} ordini', { day: dayLabel(d.day), amount: formatEuro(d.revenue_cents), n: d.orders })}>
                <div className={`bar ${d.revenue_cents ? '' : 'empty'}`} style={{ height: `${Math.max(2, (d.revenue_cents / maxDay) * 100)}%` }} />
              </div>)}</div>
          </div>
          <div className="row small muted" style={{ justifyContent: 'space-between', marginTop: 6 }}>
            <span>{dayLabel(days[0].day)}</span><span>{dayLabel(days.at(-1)!.day)}</span></div>

          <div className="card-head" style={{ marginTop: 22 }}><h2>{t('Modalità di consegna')}</h2></div>
          {fulfilmentTotal ? <>
            <div className="stack">{fulfilment.map(([k, n]) =>
              <div key={k} style={{ width: `${(n / fulfilmentTotal) * 100}%`, background: FULFILMENT_COLORS[k] }} title={`${t(FULFILMENT_LABELS[k])}: ${n}`} />)}</div>
            <div className="legend">{fulfilment.map(([k, n]) => <span key={k}><i style={{ background: FULFILMENT_COLORS[k] }} />
              {t(FULFILMENT_LABELS[k])} <strong>{n}</strong> <span className="muted">({Math.round((n / fulfilmentTotal) * 100)}%)</span></span>)}</div>
          </> : <p className="muted small">{t('Nessun ordine nel periodo.')}</p>}
        </div>

        <div className="card">
          <div className="card-head"><h2>{t('Prodotti più venduti')} <span className="small muted" style={{ fontWeight: 500 }}>{t('per pezzi')}</span></h2><Link to="/products" className="small inline-link">{t('Catalogo')} <Icon name="arrow" size={13} /></Link></div>
          {s.top_products.length ? <ol className="top-list">{s.top_products.map((p, i) => <li key={p.sku}>
            <span className="rank">{i + 1}</span>
            <div className="top-body">
              <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                <span className="top-name">{p.name}</span><span className="muted">{formatEuro(p.revenue_cents)}</span></div>
              <div className="meter" aria-hidden="true"><div style={{ width: `${(p.quantity / topMax) * 100}%` }} /></div>
              <span className="small muted"><strong>{t('{n} pezzi', { n: p.quantity })}</strong> · {p.sku}</span>
            </div>
          </li>)}</ol> : <Empty>{t('Nessuna vendita nel periodo.')}</Empty>}
        </div>
      </div>

    </>}
  </>;
}
